import React from 'react';
import { Crop as CropIcon, X, Move } from 'lucide-react';

export interface RegionalEditPanelProps {
  /** Whether region selection mode is enabled */
  isRegionSelectMode: boolean;
  /** Callback to toggle region selection mode */
  onToggleRegionMode: () => void;
  /** The currently selected region (null if none) */
  editRegion: { x: number; y: number; width: number; height: number } | null;
  /** Callback to clear the selected region */
  onClearRegion: () => void;
  /** Whether the component is in a loading state */
  isLoading?: boolean;
  /** Currently selected region aspect ratio */
  regionAspect?: number | undefined;
  /** Callback when aspect ratio changes */
  onSetRegionAspect?: (aspect: number | undefined) => void;
  /** Callback to clear region crop selection */
  onClearRegionCrop?: () => void;
  /** Description text shown below the toggle */
  description?: string;
  /** Description text shown when region mode is active */
  activeDescription?: string;
}

const ASPECT_OPTIONS = [
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:4', value: 3 / 4 },
  { label: '16:9', value: 16 / 9 },
  { label: '9:16', value: 9 / 16 },
  { label: '21:9', value: 21 / 9 },
];

const getAspectRatioStyle = (ratio: string): React.CSSProperties => {
  if (ratio === 'free') return { aspectRatio: '1/1', borderStyle: 'dashed' };
  const [w, h] = ratio.split(':').map(Number);
  return { aspectRatio: `${w}/${h}` };
};

const RegionalEditPanel: React.FC<RegionalEditPanelProps> = ({
  isRegionSelectMode,
  onToggleRegionMode,
  editRegion,
  onClearRegion,
  isLoading = false,
  regionAspect,
  onSetRegionAspect,
  onClearRegionCrop,
  description = 'Select a specific region of the image to edit instead of the entire image.',
  activeDescription,
}) => {
  return (
    <div className="bg-gray-800/40 backdrop-blur-xl border border-gray-700/50 rounded-xl p-4 shadow-lg">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <CropIcon className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-semibold text-gray-200">Regional Editing</span>
          <span className="text-xs text-gray-500 bg-gray-700/50 px-2 py-0.5 rounded-full">Beta</span>
        </div>
        <button
          onClick={onToggleRegionMode}
          disabled={isLoading}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            isRegionSelectMode ? 'bg-purple-600' : 'bg-gray-600'
          } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              isRegionSelectMode ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        {isRegionSelectMode && activeDescription ? activeDescription : description}
      </p>

      {/* Aspect ratio selector — only visible when region mode is on */}
      {isRegionSelectMode && onSetRegionAspect && (
        <div className="mt-3 pt-3 border-t border-gray-700/50">
          <label className="block text-xs font-medium text-gray-400 mb-2">Region aspect ratio</label>
          <div className="grid grid-cols-3 gap-2">
            {ASPECT_OPTIONS.map(opt => (
              <button
                key={opt.label}
                onClick={() => { onSetRegionAspect(opt.value); onClearRegionCrop?.(); }}
                className={`flex flex-col items-center justify-center px-2.5 py-2 text-xs rounded-md border transition-colors ${
                  regionAspect === opt.value
                    ? 'bg-purple-600/60 border-purple-500/70 text-white'
                    : 'bg-gray-800/60 border-gray-700/50 text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                <div
                  className={`w-5 mb-1 rounded-sm border-2 transition-colors ${
                    regionAspect === opt.value
                      ? 'border-purple-400 bg-purple-400/20'
                      : 'border-gray-500 bg-gray-500/10'
                  }`}
                  style={getAspectRatioStyle(opt.label)}
                />
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Region info */}
      {editRegion && (
        <div className="mt-3 pt-3 border-t border-gray-700/50">
          <div className="flex items-center justify-between">
            <p className="text-xs text-purple-400 flex items-center gap-2">
              <Move className="w-3 h-3" />
              Region: {editRegion.width}×{editRegion.height}px
            </p>
            <button
              onClick={onClearRegion}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-400 transition-colors"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegionalEditPanel;
